import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Activity, CheckCircle2, Loader2, X, XCircle } from 'lucide-react'
import { useBackgroundJobs } from '@/contexts/BackgroundJobsContext'

// Persistent, route-change-surviving "what's still running" indicator — the
// platform had no way to tell if a long operation (batch screen, report
// export, a Playground pivot) was still going once you navigated away from
// the page that started it, or switched to another browser tab and came
// back. This is mounted once in Layout.tsx's top bar so it's visible from
// every authenticated page.
export function BackgroundJobsIndicator() {
  const { jobs, runningCount, dismissJob } = useBackgroundJobs()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  if (jobs.length === 0) return null

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        title={runningCount > 0 ? `${runningCount} task${runningCount === 1 ? '' : 's'} running` : 'Recent background tasks'}
      >
        {runningCount > 0 ? (
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        ) : (
          <Activity className="h-4 w-4" />
        )}
        {runningCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
            {runningCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 rounded-lg border border-border bg-card shadow-2xl">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm font-semibold text-foreground">Background tasks</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Stays visible while you navigate elsewhere in the app.
            </p>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {jobs.slice().reverse().map((job) => (
              <div key={job.id} className="flex items-start gap-3 px-4 py-3 border-b border-border last:border-b-0">
                <div className="mt-0.5 flex-shrink-0">
                  {job.status === 'running' && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                  {job.status === 'done' && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                  {job.status === 'error' && <XCircle className="h-4 w-4 text-destructive" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-foreground truncate">{job.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {job.status === 'running' && 'Running…'}
                    {job.status === 'done' && (job.summary || 'Complete')}
                    {job.status === 'error' && (job.error || 'Failed')}
                  </p>
                  {job.status === 'done' && job.href && (
                    <button
                      onClick={() => {
                        // In-app routes get an SPA navigation; anything else
                        // (e.g. a generated PDF's /downloads/... link) needs
                        // a real browser navigation to actually download it.
                        if (job.href!.startsWith('/app/')) navigate(job.href!)
                        else window.open(job.href, '_blank', 'noopener')
                        setOpen(false)
                      }}
                      className="text-xs text-primary hover:underline mt-1"
                    >
                      View result →
                    </button>
                  )}
                </div>
                {job.status !== 'running' && (
                  <button
                    onClick={() => dismissJob(job.id)}
                    className="flex-shrink-0 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
